import {
  collection,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { subirACloudinary } from '@/services/cloudinary';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { MovimientoContable, Bloque, EstadoPago } from '@/types/domain';

const movConverter: FirestoreDataConverter<MovimientoContable> = {
  toFirestore(data: WithFieldValue<MovimientoContable>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<MovimientoContable, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const movimientosCollection = collection(firestore, 'movimientos').withConverter(movConverter);

export function movimientosPorBloque(bloque: Bloque): any {
  if (USE_API) return apiQuery('/movimientos', { bloque, _order: '-fecha' });
  return query(movimientosCollection, where('bloque', '==', bloque), orderBy('fecha', 'desc'));
}

export const todosMovimientosQuery: any = USE_API
  ? apiQuery('/movimientos', { _order: '-fecha' })
  : query(movimientosCollection, orderBy('fecha', 'desc'));

export interface CrearMovimientoInput {
  bloque: Bloque;
  tipo: 'ingreso' | 'egreso';
  categoria: string;
  monto: number;
  metodoPago: 'efectivo' | 'qr' | 'transferencia' | 'tarjeta' | 'otro';
  descripcion: string;
  beneficiario?: string;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  numeroFactura?: string | null;
  estadoPago?: EstadoPago;
  comprobantePagoUrl?: string | null;
  comprobantePagoNombre?: string | null;
  adjuntoUrl?: string | null; // factura / proforma
  fecha: Date;
  autorUid: string;
  autorNombre: string;
}

export async function crearMovimiento(input: CrearMovimientoInput) {
  const estadoPago: EstadoPago = input.estadoPago ?? 'pagado';
  if (USE_API) {
    const res = await api.post('/movimientos', {
      bloque: input.bloque,
      tipo: input.tipo,
      categoria: input.categoria,
      monto: input.monto,
      metodoPago: input.metodoPago,
      descripcion: input.descripcion,
      beneficiario: input.beneficiario ?? null,
      proveedorId: input.proveedorId ?? null,
      proveedorNombre: input.proveedorNombre ?? null,
      numeroFactura: input.numeroFactura ?? null,
      estadoPago,
      pagadoEn: estadoPago === 'pagado' ? input.fecha.toISOString() : null,
      comprobantePagoUrl: input.comprobantePagoUrl ?? null,
      comprobantePagoNombre: input.comprobantePagoNombre ?? null,
      adjuntoUrl: input.adjuntoUrl ?? null,
      fecha: input.fecha.toISOString(),
      autorUid: input.autorUid,
      autorNombre: input.autorNombre,
    });
    return res.id;
  }
  const id = doc(movimientosCollection).id;
  const ref = doc(movimientosCollection, id);
  await setDoc(ref, {
    ...input,
    proveedorId: input.proveedorId ?? null,
    proveedorNombre: input.proveedorNombre ?? null,
    numeroFactura: input.numeroFactura ?? null,
    estadoPago,
    pagadoEn: estadoPago === 'pagado' ? Timestamp.fromDate(input.fecha) : null,
    comprobantePagoUrl: input.comprobantePagoUrl ?? null,
    comprobantePagoNombre: input.comprobantePagoNombre ?? null,
    fecha: Timestamp.fromDate(input.fecha),
    adjuntoUrl: input.adjuntoUrl ?? null,
    createdAt: serverTimestamp(),
  } as any);
  return id;
}

/** Marca un gasto pendiente como pagado y opcionalmente adjunta comprobante. */
export async function marcarMovimientoPagado(
  id: string,
  opts?: { comprobantePagoUrl?: string | null; comprobantePagoNombre?: string | null },
) {
  if (USE_API) {
    await api.post('/acciones/marcar-pagado', {
      movimientoId: id,
      ...(opts?.comprobantePagoUrl !== undefined
        ? { comprobantePagoUrl: opts.comprobantePagoUrl ?? null, comprobantePagoNombre: opts.comprobantePagoNombre ?? null }
        : {}),
    });
    return;
  }
  const ref = doc(movimientosCollection, id);
  await updateDoc(ref, {
    estadoPago: 'pagado',
    pagadoEn: serverTimestamp(),
    ...(opts?.comprobantePagoUrl !== undefined
      ? { comprobantePagoUrl: opts.comprobantePagoUrl ?? null, comprobantePagoNombre: opts.comprobantePagoNombre ?? null }
      : {}),
  } as any);
}

/** Sube un comprobante de pago a Cloudinary. */
export async function subirComprobantePago(movimientoId: string, file: Blob, filename: string) {
  const folder = `clinicat/comprobantes`;
  const result = await subirACloudinary(file, { folder, filename: `${movimientoId}-${filename}` });
  return { url: result.url, nombre: filename };
}

export async function eliminarMovimiento(id: string) {
  if (USE_API) return void (await api.delete(`/movimientos/${id}`));
  await deleteDoc(doc(movimientosCollection, id));
}

export const CATEGORIAS_INGRESO: Record<Bloque, string[]> = {
  clinica: ['Consulta', 'Procedimiento', 'Venta producto', 'Internación', 'Vacunación', 'Otro'],
};

export const CATEGORIAS_EGRESO: Record<Bloque, string[]> = {
  clinica: ['Insumos', 'Salarios', 'Servicios', 'Mantenimiento', 'Marketing', 'Impuestos', 'Otro'],
};

export const METODOS_PAGO = ['efectivo', 'qr', 'transferencia', 'tarjeta', 'otro'] as const;
